import {
    ActionReducerMapBuilder,
    createAsyncThunk,
    createSlice,
    Draft,
    SerializedError,
} from '@reduxjs/toolkit';
import { IAction, SlicePropsWithData } from '../../store';
import { urlParamReplicaId, urlProjectSearchDocuments } from '../../../Constants';
import { IDocDefaultFields } from './sliceProjectDocuments';
import { apiFetch } from '../../../util/ProjectUtils';

const initialState: IProjectAdvancedSearchState = {
    data: [],
    loading: false,
    error: null,
    areaSearchText: '',
};

const reducers = {
    setSearchResults(state: IProjectAdvancedSearchState, action: IAction<IDocDefaultFields[]>) {
        state.data = action.payload;
    },
    setAreaSearchText(state: IProjectAdvancedSearchState, action: IAction<string>) {
        state.areaSearchText = action.payload;
    },
    resetAdvancedSearchData(state: IProjectAdvancedSearchState) {
        state.data = [];
        state.areaSearchText = '';
    },
};

const thunks = {
    getProjectAdvancedSearchDocuments: async (
        config: IProjectAdvancedSearchConfig,
    ): Promise<IDocDefaultFields[]> => {
        try {
            const { replicaId, viewName, searchTerm, fieldset } = config;

            const formData = new FormData();
            formData.append('fieldset', fieldset);
            formData.append('searchTerm', searchTerm);

            const request: RequestInit = {
                method: 'POST',
                headers: {
                    viewName,
                },
                body: formData,
            };

            const url = urlProjectSearchDocuments.replace(urlParamReplicaId, replicaId);
            const response = await apiFetch(url, request);

            return response.ok
                ? Promise.resolve(await response.json())
                : Promise.reject(new Error(await response.text()));
        } catch (e) {
            return Promise.reject(e as Error);
        }
    },

    _reducers: (builder: ActionReducerMapBuilder<IProjectAdvancedSearchState>) => {
        builder.addCase(
            getProjectAdvancedSearchDocuments.pending,
            (state: Draft<IProjectAdvancedSearchState>) => {
                state.loading = true;
                state.error = null;
            },
        );
        builder.addCase(
            getProjectAdvancedSearchDocuments.fulfilled,
            (state: Draft<IProjectAdvancedSearchState>, action: IAction<IDocDefaultFields[]>) => {
                state.loading = false;
                state.error = null;
                state.data = action.payload;
            },
        );
        builder.addCase(
            getProjectAdvancedSearchDocuments.rejected,
            (state: Draft<IProjectAdvancedSearchState>, action: IAction<SerializedError>) => {
                state.loading = false;
                state.error = action.error;
            },
        );
    },
};

export const getProjectAdvancedSearchDocuments = createAsyncThunk(
    'projectAdvancedSearch',
    thunks.getProjectAdvancedSearchDocuments,
);

interface IProjectAdvancedSearchConfig {
    replicaId: string;
    viewName: string;
    searchTerm: string;
    fieldset: string;
}

export interface IProjectAdvancedSearchState extends SlicePropsWithData<IDocDefaultFields[]> {
    areaSearchText: string;
}

export const projectAdvancedSearchSlice = createSlice({
    name: 'projectAdvancedSearch',
    extraReducers: thunks._reducers,
    initialState,
    reducers,
});

export const { setSearchResults, setAreaSearchText, resetAdvancedSearchData } =
    projectAdvancedSearchSlice.actions;

export default projectAdvancedSearchSlice.reducer;
